import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Paper,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  IconButton,
  Divider,
  Container,
  Tabs,
  Tab,
  Chip,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Autocomplete from "@mui/material/Autocomplete";
import { useParams, useNavigate } from "react-router-dom";

import { getFormByPageId, createForm, updateForm } from "../controllers/form.controller";
import { getAllTemplates, getTemplateById } from "../controllers/formTemplate.controller";

const generateId = () => "_" + Math.random().toString(36).substr(2, 9);

const DEFAULT_STYLE_COLORS = [
  { value: "#ccc", label: "خاکستری" },
  { value: "#90caf9", label: "آبی روشن" },
  { value: "#a5d6a7", label: "سبز روشن" },
  { value: "#ffe082", label: "زرد روشن" },
  { value: "#ffab91", label: "نارنجی روشن" },
  { value: "#ef9a9a", label: "قرمز روشن" },
];

const CSS_CLASS_OPTIONS = [
  { value: "", label: "بدون کلاس" },
  { value: "form-bold", label: "Bold" },
  { value: "form-italic", label: "Italic" },
  { value: "form-rtl", label: "RTL" },
  { value: "form-ltr", label: "LTR" },
];

const FONT_SIZE_OPTIONS = [
  { value: "12px", label: "کوچک" },
  { value: "14px", label: "متوسط-" },
  { value: "16px", label: "متوسط" },
  { value: "18px", label: "بزرگ" },
  { value: "20px", label: "خیلی بزرگ" },
];

/**
 * ✅ MUI v6: به جای InputLabelProps / SelectProps از slotProps استفاده کن
 * اینها برای Preview هستند تا کنترل‌ها LTR شوند.
 */
const previewControlSx = {
  direction: "ltr",
  "& .MuiInputBase-root": { direction: "ltr" },
  "& .MuiInputBase-input": { direction: "ltr", textAlign: "left" },
  "& .MuiOutlinedInput-notchedOutline": { textAlign: "left" },
};

const previewSlotProps = {
  inputLabel: {
    sx: {
      direction: "ltr",
      left: 14,
      right: "auto",
      transformOrigin: "top left",
      textAlign: "left",
    },
  },
  select: {
    MenuProps: {
      PaperProps: { sx: { direction: "ltr" } },
    },
  },
};

const defaultField = () => ({
  fieldId: generateId(),
  titlep: "",
  type: "text",
  required: false,

  options: [],
  newOption: "",

  // Style config
  backgroundColor: "#ccc",
  cssClass: "",
  fontSize: "16px",

  // Grouping & steps
  stepNumber: 1,
  group: "",

  // extra
  tooltip: "",
  maxStars: 5,
  backgroundImage: "",
  penColor: "#000000",
  penSize: 3,
  matrixRows: [],
  matrixCols: [],

  // Conditional visibility
  visibilityCondition: null, // { parentFieldId, expectedValue }

  // Compare configs
  compareEnabled: false,
  compareDisplay: "table",
});

/**
 * ✅ فقط همین‌ها (اسم و value دقیقاً همون)
 */
const FIELD_TYPES = [
  { value: "text", label: "📝 متن" },
  { value: "number", label: "🔢 عدد" },
  { value: "select", label: "📋 لیست کشویی" },
  { value: "checkbox", label: "☑ چندگزینه‌ای" },
  { value: "file", label: "📎 فایل" },
  { value: "persian_date", label: "📅 تاریخ شمسی" },
  { value: "rating", label: "⭐️ امتیازدهی (Rating)" },
  { value: "drawing", label: "🎨 نقاشی روی تصویر" },
  { value: "matrix", label: "🗒️ ماتریس چندگزینه‌ای" },
  { value: "calculated", label: "➕ محاسبه‌ای (Sum/Avg)" },
];

const typeLabel = (t) => FIELD_TYPES.find((x) => x.value === t)?.label || t;

/**
 * ✅ Preview Input Factory (برای اینکه همه کنترل‌ها LTR باشند + بدون deprecated props)
 */
function PreviewTextField(props) {
  return (
    <TextField
      {...props}
      size="small"
      sx={{ ...(props.sx || {}), ...previewControlSx }}
      slotProps={{
        ...(props.slotProps || {}),
        inputLabel: {
          ...(previewSlotProps.inputLabel || {}),
          ...((props.slotProps && props.slotProps.inputLabel) || {}),
        },
      }}
    />
  );
}

function PreviewSelectField({ label, value, children, ...rest }) {
  return (
    <TextField
      select
      fullWidth
      size="small"
      value={value}
      label={label}
      sx={previewControlSx}
      slotProps={{
        inputLabel: previewSlotProps.inputLabel,
        select: previewSlotProps.select,
      }}
      {...rest}
    >
      {children}
    </TextField>
  );
}

export default function FormBuilderPage() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:900px)");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(null);
  const [formtitle, setFormTitle] = useState({ fa: "", en: "" });

  const [fields, setFields] = useState([]);
  const [selectedFieldId, setSelectedFieldId] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [mobileTab, setMobileTab] = useState(0);

  const ensureSelected = (nextFields) => {
    if (!nextFields?.length) {
      setSelectedFieldId(null);
      return;
    }
    const exists = nextFields.some((f) => f.fieldId === selectedFieldId);
    if (!exists) setSelectedFieldId(nextFields[0].fieldId);
  };

  // گروه‌بندی برای پیش‌نمایش (مرحله + گروه)
  const grouped = useMemo(() => {
    const groupedObj = {};
    fields.forEach((field) => {
      const step = Number(field.stepNumber || 1);
      const group = field.group?.trim() || "بدون گروه";

      if (!groupedObj[step]) groupedObj[step] = {};
      if (!groupedObj[step][group]) groupedObj[step][group] = [];
      groupedObj[step][group].push(field);
    });

    const steps = Object.keys(groupedObj)
      .map((x) => Number(x))
      .sort((a, b) => a - b);

    return steps.map((s) => ({
      step: s,
      groups: groupedObj[s],
    }));
  }, [fields]);

  const fetchForm = async () => {
    try {
      setLoading(true);

      const existing = await getFormByPageId(pageId);

      if (existing) {
        setForm(existing);
        setFormTitle(existing.title || { fa: "", en: "" });

        const mapped = (existing.structure || []).map((f) => ({
          ...defaultField(),
          ...f,
          fontSize: f.style?.font || f.fontSize || "16px",
          backgroundColor: f.style?.color || f.backgroundColor || "#ccc",
          cssClass: f.style?.className || f.cssClass || "",
          group: f.group || "",
          compareEnabled: !!f.compareEnabled,
          compareDisplay: f.compareDisplay || "table",
        }));

        if (!mapped.length) {
          const first = defaultField();
          setFields([first]);
          setSelectedFieldId(first.fieldId);
        } else {
          setFields(mapped);
          setSelectedFieldId(mapped[0].fieldId);
        }
      } else {
        const created = await createForm({
          pageId,
          title: { fa: "فرم جدید", en: "New Form" },
          structure: [],
          isActive: true,
          allowMultipleSubmissions: false,
          collectUserInfo: true,
        });
        setForm(created);
        setFormTitle(created?.title || { fa: "فرم جدید", en: "New Form" });

        const first = defaultField();
        setFields([first]);
        setSelectedFieldId(first.fieldId);
      }
    } catch (e) {
      console.error(e);
      toast.error("خطا در دریافت اطلاعات فرم");
      const first = defaultField();
      setFields([first]);
      setSelectedFieldId(first.fieldId);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const list = await getAllTemplates();
      setTemplates(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchForm();
    fetchTemplates();
    // eslint-disable-next-line
  }, [pageId]);

  const addField = () => {
    const nf = defaultField();
    const next = [...fields, nf];
    setFields(next);
    setSelectedFieldId(nf.fieldId);
    toast.info("فیلد جدید اضافه شد");
  };

  const deleteField = (fieldId) => {
    const next = fields.filter((f) => f.fieldId !== fieldId);
    if (!next.length) {
      const first = defaultField();
      setFields([first]);
      setSelectedFieldId(first.fieldId);
      toast.info("حداقل یک فیلد لازم است؛ یک فیلد جدید ایجاد شد.");
      return;
    }
    setFields(next);
    ensureSelected(next);
    toast.success("فیلد حذف شد");
  };

  const updateField = (fieldId, patch) => {
    setFields((prev) => prev.map((f) => (f.fieldId === fieldId ? { ...f, ...patch } : f)));
  };

  const addOption = (fieldId) => {
    const f = fields.find((x) => x.fieldId === fieldId);
    const newOption = (f?.newOption || "").trim();
    if (!newOption) return;

    const nextOptions = Array.isArray(f.options) ? [...f.options, newOption] : [newOption];
    updateField(fieldId, { options: nextOptions, newOption: "" });
  };

  const removeOption = (fieldId, idx) => {
    const f = fields.find((x) => x.fieldId === fieldId);
    const nextOptions = (f?.options || []).filter((_, i) => i !== idx);
    updateField(fieldId, { options: nextOptions });
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (!formtitle?.fa?.trim()) {
        toast.warning("عنوان فارسی فرم را وارد کن");
        return;
      }
      if (!form?._id) {
        toast.error("فرم هنوز ساخته نشده یا شناسه ندارد");
        return;
      }

      const safeFields = fields.map((f) => ({ ...f, fieldId: f.fieldId || generateId() }));

      const prepared = safeFields.map((f) => ({
        fieldId: f.fieldId,
        titlep: f.titlep,
        type: f.type,
        required: !!f.required,
        tooltip: f.tooltip || "",

        stepNumber: f.stepNumber || 1,
        group: (f.group || "").trim(),

        options: f.options || [],
        maxStars: f.maxStars || 5,

        backgroundImage: f.backgroundImage || "",
        penColor: f.penColor || "#000000",
        penSize: f.penSize || 3,
        matrixRows: f.matrixRows || [],
        matrixCols: f.matrixCols || [],

        visibilityCondition: f.visibilityCondition || null,

        compareEnabled: !!f.compareEnabled,
        compareDisplay: f.compareDisplay || "table",

        style: {
          font: f.fontSize || "16px",
          color: f.backgroundColor || "#ccc",
          className: f.cssClass || "",
        },
      }));

      await updateForm(form._id, {
        title: formtitle,
        isActive: true,
        allowMultipleSubmissions: true,
        structure: prepared,
      });

      setFields(safeFields);
      toast.success("✅ فرم با موفقیت ذخیره شد");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "❌ خطا در ذخیره فرم");
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = async () => {
    try {
      if (!selectedTemplate?._id) return;

      const t = await getTemplateById(selectedTemplate._id);
      const templateFields = t?.structure || t?.detail?.fields || t?.fields || [];

      if (!Array.isArray(templateFields) || !templateFields.length) {
        toast.warning("این قالب فیلدی ندارد");
        return;
      }

      const cloned = templateFields.map((f) => ({
        ...defaultField(),
        ...f,
        fieldId: generateId(),
        fontSize: f.style?.font || f.fontSize || "16px",
        backgroundColor: f.style?.color || f.backgroundColor || "#ccc",
        cssClass: f.style?.className || f.cssClass || "",
      }));

      setFields(cloned);
      setSelectedFieldId(cloned[0].fieldId);
      setOpenTemplateDialog(false);
      toast.success("قالب اعمال شد");
    } catch (e) {
      console.error(e);
      toast.error("خطا در اعمال قالب");
    }
  };

  // ---- Controls for visibilityCondition
  const candidateParents = useMemo(() => {
    // فقط فیلدهایی که گزینه دارند منطقی‌اند برای شرط
    return fields
      .filter((f) => ["select", "checkbox"].includes(f.type) && (f.options?.length || 0) > 0)
      .map((f) => ({
        fieldId: f.fieldId,
        title: f.titlep || "بدون عنوان",
        options: f.options || [],
      }));
  }, [fields]);

  // ---------- UI Blocks ----------
  const HeaderBar = (
    <Paper
      elevation={0}
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        borderRadius: 0,
        borderBottom: "1px solid rgba(0,0,0,0.08)",
        background: "linear-gradient(180deg, rgba(33,150,243,0.10) 0%, rgba(255,255,255,0.96) 60%)",
        backdropFilter: "blur(8px)",
      }}
    >
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 999, backgroundColor: "primary.main" }} />
              <Typography fontWeight={900} sx={{ fontSize: 18 }}>
                طراحی فرم
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              ساخت و مدیریت فیلدها + پیش‌نمایش زنده
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
            <TextField
              size="small"
              label="عنوان فارسی"
              value={formtitle.fa || ""}
              onChange={(e) => setFormTitle((p) => ({ ...p, fa: e.target.value }))}
              sx={{ width: { xs: "100%", sm: 220 } }}
            />
            <TextField
              size="small"
              label="Form Title (English)"
              value={formtitle.en || ""}
              onChange={(e) => setFormTitle((p) => ({ ...p, en: e.target.value }))}
              sx={{ width: { xs: "100%", sm: 220 } }}
            />

            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addField} sx={{ whiteSpace: "nowrap" }}>
                فیلد جدید
              </Button>

              <Button
                variant="outlined"
                startIcon={<ContentPasteIcon />}
                onClick={() => setOpenTemplateDialog(true)}
                sx={{ whiteSpace: "nowrap" }}
              >
                قالب آماده
              </Button>

              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ whiteSpace: "nowrap", fontWeight: 900 }}
              >
                ذخیره فرم
              </Button>
            </Stack>
          </Stack>
        </Stack>

        {isMobile && (
          <Box sx={{ mt: 1 }}>
            <Tabs
              value={mobileTab}
              onChange={(_, v) => setMobileTab(v)}
              variant="fullWidth"
              sx={{ "& .MuiTab-root": { fontWeight: 800 } }}
            >
              <Tab icon={<EditIcon />} iconPosition="start" label="ویرایش" />
              <Tab icon={<VisibilityIcon />} iconPosition="start" label="پیش‌نمایش" />
            </Tabs>
          </Box>
        )}
      </Container>
    </Paper>
  );

  const FieldCard = (f) => {
    const isActive = f.fieldId === selectedFieldId;

    const parentOpt = f.visibilityCondition?.parentFieldId
      ? candidateParents.find((x) => x.fieldId === f.visibilityCondition.parentFieldId)
      : null;

    return (
      <Accordion
        key={f.fieldId}
        expanded={isActive}
        onChange={() => setSelectedFieldId(f.fieldId)}
        disableGutters
        elevation={0}
        sx={{
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: 2,
          overflow: "hidden",
          mb: 1,
          "&:before": { display: "none" },
          boxShadow: isActive ? "0 8px 30px rgba(33,150,243,0.12)" : "none",
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 999,
                backgroundColor: isActive ? "primary.main" : "rgba(0,0,0,0.18)",
              }}
            />
            <Typography fontWeight={800} sx={{ flexGrow: 1, fontSize: 14 }}>
              {f.titlep?.trim() ? f.titlep : "بدون عنوان"}
            </Typography>

            <Chip size="small" label={typeLabel(f.type)} variant="outlined" />
            <Chip size="small" label={`مرحله ${f.stepNumber || 1}`} />

            <Tooltip title="حذف فیلد">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteField(f.fieldId);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <Stack spacing={1.25}>
            <TextField
              label="صورت سوال"
              value={f.titlep || ""}
              onChange={(e) => updateField(f.fieldId, { titlep: e.target.value })}
              fullWidth
              size="small"
              InputProps={{
                style: {
                  fontSize: f.fontSize,
                  fontWeight: f.cssClass === "form-bold" ? "bold" : "normal",
                  fontStyle: f.cssClass === "form-italic" ? "italic" : "normal",
                },
              }}
            />

            <TextField
              select
              label="نوع پاسخ"
              value={f.type}
              onChange={(e) => updateField(f.fieldId, { type: e.target.value })}
              fullWidth
              size="small"
            >
              {/* ✅ دقیقاً همین‌ها */}
              <MenuItem value="text">📝 متن</MenuItem>
              <MenuItem value="number">🔢 عدد</MenuItem>
              <MenuItem value="select">📋 لیست کشویی</MenuItem>
              <MenuItem value="checkbox">☑ چندگزینه‌ای</MenuItem>
              <MenuItem value="file">📎 فایل</MenuItem>
              <MenuItem value="persian_date">📅 تاریخ شمسی</MenuItem>
              <MenuItem value="rating">⭐️ امتیازدهی (Rating)</MenuItem>
              <MenuItem value="drawing">🎨 نقاشی روی تصویر</MenuItem>
              <MenuItem value="matrix">🗒️ ماتریس چندگزینه‌ای</MenuItem>
              <MenuItem value="calculated">➕ محاسبه‌ای (Sum/Avg)</MenuItem>
            </TextField>

            {/* تنظیمات نمایش */}
            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, backgroundColor: "rgba(0,0,0,0.02)" }}>
              <Typography fontWeight={900} sx={{ fontSize: 13, mb: 1 }}>
                تنظیمات نمایش
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  select
                  label="رنگ"
                  value={f.backgroundColor || "#ccc"}
                  onChange={(e) => updateField(f.fieldId, { backgroundColor: e.target.value })}
                  size="small"
                  sx={{ width: { xs: "100%", sm: 220 } }}
                >
                  {DEFAULT_STYLE_COLORS.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="CSS کلاس"
                  value={f.cssClass || ""}
                  onChange={(e) => updateField(f.fieldId, { cssClass: e.target.value })}
                  size="small"
                  sx={{ width: { xs: "100%", sm: 220 } }}
                >
                  {CSS_CLASS_OPTIONS.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="فونت"
                  value={f.fontSize || "16px"}
                  onChange={(e) => updateField(f.fieldId, { fontSize: e.target.value })}
                  size="small"
                  sx={{ width: { xs: "100%", sm: 220 } }}
                >
                  {FONT_SIZE_OPTIONS.map((fs) => (
                    <MenuItem key={fs.value} value={fs.value}>
                      {fs.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
                <TextField
                  label="گروه (دسته‌بندی)"
                  value={f.group || ""}
                  onChange={(e) => updateField(f.fieldId, { group: e.target.value })}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="مرحله (شماره)"
                  value={f.stepNumber ?? 1}
                  onChange={(e) => updateField(f.fieldId, { stepNumber: Number(e.target.value || 1) })}
                  size="small"
                  sx={{ width: { xs: "100%", sm: 180 } }}
                />
              </Stack>
            </Paper>

            <FormControlLabel
              control={<Checkbox checked={!!f.required} onChange={(e) => updateField(f.fieldId, { required: e.target.checked })} />}
              label="الزامی باشد؟"
            />

            <TextField
              label="راهنمای نمایش (Tooltip)"
              value={f.tooltip || ""}
              onChange={(e) => updateField(f.fieldId, { tooltip: e.target.value })}
              fullWidth
              size="small"
            />

            {/* نمایش شرطی */}
            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
              <Typography fontWeight={900} sx={{ fontSize: 13, mb: 1 }}>
                نمایش شرطی
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!f.visibilityCondition}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateField(f.fieldId, { visibilityCondition: { parentFieldId: "", expectedValue: "" } });
                      } else {
                        updateField(f.fieldId, { visibilityCondition: null });
                      }
                    }}
                  />
                }
                label="نمایش این سوال فقط در صورت پاسخ خاص به یک سوال دیگر"
              />

              {!!f.visibilityCondition && (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <TextField
                    select
                    size="small"
                    label="اگر این سوال پاسخ داده شود"
                    value={f.visibilityCondition.parentFieldId || ""}
                    onChange={(e) =>
                      updateField(f.fieldId, {
                        visibilityCondition: {
                          ...f.visibilityCondition,
                          parentFieldId: e.target.value,
                          expectedValue: "",
                        },
                      })
                    }
                    fullWidth
                  >
                    <MenuItem value="">— انتخاب سوال —</MenuItem>
                    {candidateParents.map((p) => (
                      <MenuItem key={p.fieldId} value={p.fieldId}>
                        {p.title}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="و مقدار برابر باشد با"
                    value={f.visibilityCondition.expectedValue || ""}
                    onChange={(e) =>
                      updateField(f.fieldId, {
                        visibilityCondition: { ...f.visibilityCondition, expectedValue: e.target.value },
                      })
                    }
                    fullWidth
                    disabled={!f.visibilityCondition.parentFieldId}
                  >
                    <MenuItem value="">— انتخاب مقدار —</MenuItem>
                    {(parentOpt?.options || []).map((opt, i) => (
                      <MenuItem key={i} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              )}
            </Paper>

            {/* options فقط برای select */}
            {(f.type === "select" || f.type === "checkbox") && (
              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, backgroundColor: "rgba(0,0,0,0.02)" }}>
                <Typography fontWeight={900} sx={{ fontSize: 13, mb: 1 }}>
                  گزینه‌ها
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField
                    label="گزینه جدید"
                    value={f.newOption || ""}
                    onChange={(e) => updateField(f.fieldId, { newOption: e.target.value })}
                    size="small"
                    fullWidth
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addOption(f.fieldId);
                    }}
                  />
                  <Button variant="contained" onClick={() => addOption(f.fieldId)} sx={{ whiteSpace: "nowrap", fontWeight: 900 }}>
                    افزودن
                  </Button>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                  {(f.options || []).map((opt, idx) => (
                    <Chip key={`${opt}-${idx}`} label={opt} onDelete={() => removeOption(f.fieldId, idx)} sx={{ mb: 1 }} />
                  ))}
                </Stack>
              </Paper>
            )}

            {/* rating */}
            {f.type === "rating" && (
              <TextField
                label="حداکثر ستاره"
                value={f.maxStars ?? 5}
                onChange={(e) => updateField(f.fieldId, { maxStars: Number(e.target.value || 5) })}
                size="small"
                sx={{ width: { xs: "100%", sm: 220 } }}
              />
            )}

            {/* matrix editor */}
            {f.type === "matrix" && (
              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, backgroundColor: "rgba(0,0,0,0.02)" }}>
                <Typography fontWeight={900} sx={{ fontSize: 13, mb: 1 }}>
                  تنظیمات ماتریس
                </Typography>

                {/* ردیف‌ها */}
                <Typography fontWeight={800} sx={{ fontSize: 12, mb: 0.5 }}>
                  ردیف‌ها
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField
                    label="ردیف جدید"
                    value={f.newRow || ""}
                    onChange={(e) => updateField(f.fieldId, { newRow: e.target.value })}
                    size="small"
                    fullWidth
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const v = (f.newRow || "").trim();
                        if (!v) return;
                        updateField(f.fieldId, { matrixRows: [...(f.matrixRows || []), v], newRow: "" });
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => {
                      const v = (f.newRow || "").trim();
                      if (!v) return;
                      updateField(f.fieldId, { matrixRows: [...(f.matrixRows || []), v], newRow: "" });
                    }}
                    sx={{ whiteSpace: "nowrap", fontWeight: 900 }}
                  >
                    افزودن ردیف
                  </Button>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                  {(f.matrixRows || []).map((row, idx) => (
                    <Chip
                      key={`${row}-${idx}`}
                      label={row}
                      onDelete={() => updateField(f.fieldId, { matrixRows: (f.matrixRows || []).filter((_, i) => i !== idx) })}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                {/* ستون‌ها */}
                <Typography fontWeight={800} sx={{ fontSize: 12, mb: 0.5 }}>
                  ستون‌ها
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField
                    label="ستون جدید"
                    value={f.newCol || ""}
                    onChange={(e) => updateField(f.fieldId, { newCol: e.target.value })}
                    size="small"
                    fullWidth
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const v = (f.newCol || "").trim();
                        if (!v) return;
                        updateField(f.fieldId, { matrixCols: [...(f.matrixCols || []), v], newCol: "" });
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => {
                      const v = (f.newCol || "").trim();
                      if (!v) return;
                      updateField(f.fieldId, { matrixCols: [...(f.matrixCols || []), v], newCol: "" });
                    }}
                    sx={{ whiteSpace: "nowrap", fontWeight: 900 }}
                  >
                    افزودن ستون
                  </Button>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                  {(f.matrixCols || []).map((col, idx) => (
                    <Chip
                      key={`${col}-${idx}`}
                      label={col}
                      onDelete={() => updateField(f.fieldId, { matrixCols: (f.matrixCols || []).filter((_, i) => i !== idx) })}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              </Paper>
            )}


            {/* تنظیمات مقایسه */}
            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
              <Typography fontWeight={900} sx={{ fontSize: 13, mb: 1 }}>
                مقایسه بیماران
              </Typography>

              <FormControlLabel
                control={<Checkbox checked={!!f.compareEnabled} onChange={(e) => updateField(f.fieldId, { compareEnabled: e.target.checked })} />}
                label="این فیلد در مقایسه استفاده شود"
              />

              {!!f.compareEnabled && (
                <TextField
                  select
                  label="نوع نمایش در مقایسه"
                  value={f.compareDisplay || "table"}
                  onChange={(e) => updateField(f.fieldId, { compareDisplay: e.target.value })}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                >
                  <MenuItem value="table">📋 جدول</MenuItem>
                  <MenuItem value="number-summary">🔢 خلاصه عددی (min/max/avg)</MenuItem>
                  <MenuItem value="bar">📊 نمودار میله‌ای</MenuItem>
                  <MenuItem value="radar">🕸 نمودار راداری</MenuItem>
                  <MenuItem value="pie">🥧 نمودار دایره‌ای</MenuItem>
                </TextField>
              )}
            </Paper>
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  const EditorPanel = (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        height: isMobile ? "auto" : "calc(100vh - 170px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontWeight={900}>ویرایشگر فیلدها</Typography>
          <Chip size="small" label={`${fields.length} فیلد`} sx={{ fontWeight: 900 }} color="primary" variant="outlined" />
        </Stack>
      </Box>

      <Box
        sx={{
          p: 2,
          overflow: "auto",
          flex: 1,
          background: "linear-gradient(180deg, rgba(33,150,243,0.04) 0%, rgba(255,255,255,1) 25%)",
        }}
      >
        {fields.map((f) => FieldCard(f))}
      </Box>
    </Paper>
  );


  const PreviewPanel = (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        height: isMobile ? "auto" : "calc(100vh - 170px)",
        display: "flex",
        flexDirection: "column",
        position: "sticky",
        top: 110,
      }}
    >
      <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontWeight={900}>پیش‌نمایش زنده</Typography>
          <Chip size="small" icon={<VisibilityIcon />} label="Live" />
        </Stack>
      </Box>

      <Box sx={{ p: 2, overflow: "auto", flex: 1 }}>
        {grouped.map(({ step, groups }) => (
          <Box key={step} sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography fontWeight={900} sx={{ fontSize: 14, direction: "rtl", textAlign: "right" }}>
                مرحله {step}
              </Typography>
            </Stack>

            {Object.entries(groups).map(([groupName, gFields]) => (
              <Box key={groupName} sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontWeight: 900,
                    backgroundColor: "rgba(33,150,243,0.08)",
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    borderRight: "5px solid rgba(33,150,243,0.8)",
                    mb: 1,
                    direction: "rtl",
                    textAlign: "right",
                  }}
                >
                  📁 گروه: {groupName}
                </Typography>

                <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
                  {gFields.map((f) => {
                    const titleDirection = f.cssClass === "form-ltr" ? "ltr" : "rtl";
                    const fw = f.cssClass === "form-bold" ? 800 : 600;
                    const fs = f.fontSize || "16px";

                    return (
                      <Box key={f.fieldId} sx={{ mb: 1.4 }}>
                        <Typography
                          sx={{
                            fontWeight: fw,
                            fontSize: fs,
                            direction: titleDirection,
                            textAlign: titleDirection === "ltr" ? "left" : "right",
                            borderLeft: `4px solid ${f.backgroundColor || "#ccc"}`,
                            paddingLeft: 8,
                            mb: 0.5,
                          }}
                        >
                          {f.titlep || "بدون عنوان"}
                          {f.required ? <span style={{ color: "red" }}> *</span> : null}
                          {f.tooltip ? (
                            <span
                              title={f.tooltip}
                              style={{
                                marginInlineStart: 6,
                                color: "#999",
                                cursor: "help",
                              }}
                            >
                              ❔
                            </span>
                          ) : null}
                        </Typography>

                        {/* ✅ Controls: LTR */}
                        {f.type === "text" ? (
                          <PreviewTextField fullWidth placeholder="..." />
                        ) : f.type === "number" ? (
                          <PreviewTextField fullWidth type="number" placeholder="0" />
                        ) : f.type === "select" ? (
                          <PreviewSelectField value="" label="Choose">
                            {(f.options || []).map((opt, idx) => (
                              <MenuItem key={idx} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </PreviewSelectField>
                        ) : f.type === "checkbox" ? (
                          <Stack sx={{ direction: "ltr" }} spacing={0.5}>
                            {(f.options || []).length ? (
                              (f.options || []).map((opt, idx) => (
                                <FormControlLabel key={idx} control={<Checkbox />} label={opt} />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ direction: "rtl", textAlign: "right" }}>
                                (گزینه‌ای تعریف نشده)
                              </Typography>
                            )}
                          </Stack>
                        ) : f.type === "file" ? (
                          <PreviewTextField fullWidth placeholder="Select file..." />
                        ) : f.type === "persian_date" ? (
                          <PreviewTextField fullWidth placeholder="۱۴۰۳/۰۱/۰۱" />
                        ) : f.type === "rating" ? (
                          <Typography variant="body2" color="text.secondary" sx={{ direction: "rtl", textAlign: "right" }}>
                            امتیازدهی (۱ تا {f.maxStars || 5})
                          </Typography>
                        ) : f.type === "drawing" ? (
                          <Typography variant="body2" color="text.secondary" sx={{ direction: "rtl", textAlign: "right" }}>
                            نقاشی روی تصویر (نمایش ساده)
                          </Typography>
                        ) : f.type === "matrix" ? (
                          <Box sx={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                              <thead>
                                <tr>
                                  <th style={{ border: "1px solid #ccc", padding: 6 }}></th>
                                  {(f.matrixCols || []).map((col, i) => (
                                    <th key={i} style={{ border: "1px solid #ccc", padding: 6, textAlign: "center" }}>
                                      {col}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(f.matrixRows || []).map((row, i) => (
                                  <tr key={i}>
                                    <td style={{ border: "1px solid #ccc", padding: 6 }}>{row}</td>
                                    {(f.matrixCols || []).map((_, j) => (
                                      <td key={j} style={{ border: "1px solid #ccc", padding: 6, textAlign: "center" }}>
                                        <input type="radio" disabled />
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </Box>
                        ) : f.type === "calculated" ? (
                          <Typography variant="body2" color="text.secondary" sx={{ direction: "rtl", textAlign: "right" }}>
                            فیلد محاسبه‌ای (Sum/Avg) (نمایش ساده)
                          </Typography>
                        ) : (
                          <PreviewTextField fullWidth placeholder="..." />
                        )}

                        <Divider sx={{ mt: 1.2 }} />
                      </Box>
                    );
                  })}
                </Paper>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Paper>
  );

  // ---------- Render ----------
  return (
    <Box dir="rtl" sx={{ minHeight: "100vh", backgroundColor: "#f6f9fc" }}>
      <ToastContainer position="top-center" />

      {HeaderBar}

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {loading ? (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }} color="text.secondary">
              در حال بارگذاری فرم...
            </Typography>
          </Paper>
        ) : (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button variant="text" onClick={() => navigate(-1)}>
                ⬅ بازگشت
              </Button>
            </Stack>

            {!isMobile ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 0.9fr",
                  gap: 2,
                  alignItems: "start",
                }}
              >
                {EditorPanel}
                {PreviewPanel}
              </Box>
            ) : (
              <Box>{mobileTab === 0 ? EditorPanel : PreviewPanel}</Box>
            )}
          </>
        )}
      </Container>

      {/* Template Dialog */}
      <Dialog open={openTemplateDialog} onClose={() => setOpenTemplateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>استفاده از قالب آماده</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            یک قالب انتخاب کن تا فیلدهای آن روی فرم اعمال شود.
          </Typography>

          <Autocomplete
            options={templates}
            getOptionLabel={(opt) => opt?.title?.fa || opt?.title || "—"}
            value={selectedTemplate}
            onChange={(_, v) => setSelectedTemplate(v)}
            renderInput={(params) => <TextField {...params} label="انتخاب قالب" />}
          />

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={() => setOpenTemplateDialog(false)}>انصراف</Button>
            <Button variant="contained" onClick={applyTemplate} disabled={!selectedTemplate} sx={{ fontWeight: 900 }}>
              اعمال قالب
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
